import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Button, Chip, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { memo, useMemo } from "react";
import FlexBox from "../../layouts/Flexbox";
import {
  format24hLabelFromMinutes,
  SLOT_MODAL_EMPTY_PLACEHOLDER_PT,
  SLOT_MODAL_EMPTY_PLACEHOLDER_PX,
  type TimeSlotMeta,
} from "./utils";
import "./style.css";
export type SelectedSlotValidationRef = {
  dateKey: string;
  slotIndex: number;
};

export type SelectedTimesReviewProps = {
  slots: TimeSlotMeta[];
  selectionsByDateKey: Record<string, number[]>;
  onSelectionsChange: (next: Record<string, number[]>) => void;
  validationPassedBySlotKey: ReadonlyMap<string, boolean>;
  focusedSlotRef: SelectedSlotValidationRef | null;
  onFocusSlot: (ref: SelectedSlotValidationRef) => void;
  onRemoveDateKey?: (dateKey: string) => void;
};

function SelectedTimesReview({
  slots,
  selectionsByDateKey,
  onSelectionsChange,
  validationPassedBySlotKey,
  focusedSlotRef,
  onFocusSlot,
  onRemoveDateKey,
}: SelectedTimesReviewProps) {
  const datesWithSelections = useMemo(() => {
    const keys = Object.keys(selectionsByDateKey).filter(
      (k) => (selectionsByDateKey[k]?.length ?? 0) > 0
    );
    keys.sort();
    return keys.map((k) => ({
      dateKey: k,
      indices: [...(selectionsByDateKey[k] ?? [])].sort((a, b) => a - b),
    }));
  }, [selectionsByDateKey]);

  const removeSlotFromDate = (dateKey: string, slotIndex: number) => {
    const next = { ...selectionsByDateKey };
    const list = (next[dateKey] ?? []).filter((i) => i !== slotIndex);
    if (list.length === 0) delete next[dateKey];
    else next[dateKey] = list;
    onSelectionsChange(next);
  };

  if (datesWithSelections.length === 0) {
    return (
      <Stack pt={SLOT_MODAL_EMPTY_PLACEHOLDER_PT} px={SLOT_MODAL_EMPTY_PLACEHOLDER_PX} />
    );
  }

  const formatSelectedChipLabel = (slot: TimeSlotMeta | undefined) => {
    if (!slot) return "";
    return format24hLabelFromMinutes(slot.minutesFromMidnight);
  };

  return (
    <Stack
      height="100%"
      gap={1.25}
      minHeight={0}
    >
      {datesWithSelections.map(({ dateKey, indices }) => (
        <Stack
          key={dateKey}
          role="group"
          aria-label={dayjs(dateKey).format("MMMM D, YYYY")}
          gap={1.25}
          className="selectedTimesDateCard"
        >
          <FlexBox alignItems="center" justifyContent="space-between" gap={1}>
            <Typography
              variant="body-ibmPlexMono-medium"
              color="text.secondary"
              className="selectedTimesHeaderDate"
            >
              {dayjs(dateKey).format("D MMM , YYYY")}
            </Typography>
            {onRemoveDateKey ? (
              <Button
                type="button"
                size="small"
                color="error"
                variant="text"
                onClick={() => onRemoveDateKey(dateKey)}
                className="selectedTimesRemoveBtn"
              >
                <Typography
                  component="span"
                  variant="small-ibmPlexMono"
                  color="error.dark"
                  className="selectedTimesRemoveText"
                >
                  - Remove
                </Typography>
              </Button>
            ) : null}
          </FlexBox>

          <FlexBox
            component="div"
            role="list"
            aria-label={`Times for ${dayjs(dateKey).format("MMM D")}`}
            gap={0.5}
            flexWrap="wrap"
            width="100%"
          >
            {indices.map((slotIndex) => {
              const meta = slots[slotIndex];
              const label = formatSelectedChipLabel(meta);
              const compositeKey = `${dateKey}:${slotIndex}`;
              const passesValidation =
                validationPassedBySlotKey.get(compositeKey) !== false;

              return (
                <Chip
                  key={compositeKey}
                  role="listitem"
                  label={
                    <Typography
                      component="span"
                      variant="small-ibmPlexMono-medium"
                      fontWeight={500}
                      color="inherit"
                      noWrap
                      className="selectedTimesChipTimeText"
                    >
                      {label}
                    </Typography>
                  }
                  size="small"
                  variant="filled"
                  onClick={() => onFocusSlot({ dateKey, slotIndex })}
                  className={[
                    "selectedTimesChip",
                    passesValidation ? undefined : "selectedTimesChipInvalid",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  classes={{
                    label: "selectedTimesChipLabel",
                    deleteIcon: "slotModalChipDeleteIcon",
                  }}
                  deleteIcon={<CloseRoundedIcon />}
                  onDelete={(e) => {
                    e.stopPropagation();
                    removeSlotFromDate(dateKey, slotIndex);
                  }}
                />
              );
            })}
          </FlexBox>
        </Stack>
      ))}
    </Stack>
  );
}

export default memo(SelectedTimesReview);
